import React, { useRef, useState, useEffect } from "react";

// Adaptive Navigation Layout Hook
// 
// Large screens: Navigation items get full natural width (calculated mathematically)
// Constrained screens: Items get equal width distribution (itemWidth)
// Real-time adaptation: Recalculates on window resize using character-based width estimation
// Conditional rendering: Only runs when user is loaded and has access
// 
// Performance: Uses mathematical text width calculation instead of DOM measurement
// This creates a smooth, adaptive navigation that automatically switches between 
// expansive and condensed layouts without manual breakpoints.

export const useNavLayout = (_Nav, isLargeScreen, hasPrivateAccess, isUserLoading) => {
  
  const headerRef = useRef(null);
  const logoRef = useRef(null);
  const navRef = useRef(null);
  const rightRef = useRef(null);
  const [navLayout, setNavLayout] = useState({
    isOverflowing: false,
    itemWidth: 0,
    availableWidth: 0
  });

  useEffect(() => {
    function calculateNavLayout() {
      if (!headerRef.current || !logoRef.current || !navRef.current || !rightRef.current) return;

      const headerWidth = headerRef.current.offsetWidth;
      const logoWidth = logoRef.current.offsetWidth;
      const rightWidth = rightRef.current.offsetWidth;
      
      const padding = 12;

      // Available Space Calculation
      // Measures the actual space left for navigation after accounting for logo and right-side elements.
      const availableWidth = headerWidth - logoWidth - rightWidth - padding;

      // Width Estimation: Character-based calculation (replaces DOM measurement for performance)
      // This approach calculates text width mathematically instead of creating temporary DOM elements
      let totalNaturalWidth = 0;
      if (_Nav.primary && Array.isArray(_Nav.primary)) {
        const CHAR_WIDTH = 8.5; // Average character width for 14px font-weight 500
        const WIDE_CHAR_MULTIPLIER = 1.2; // Multiplier for wider characters (W, M, Q, O, G, D, @)
        
        _Nav.primary.forEach(item => {
          const text = item.display || item.label || item.value || '';
          
          // Text Width Calculation:
          // - Count wide characters that need extra space
          // - Apply base width to normal chars, multiplied width to wide chars
          const wideCharCount = (text.match(/[WMQOGD@]/g) || []).length;
          const normalCharCount = text.length - wideCharCount;
          const estimatedTextWidth = (normalCharCount * CHAR_WIDTH) + (wideCharCount * CHAR_WIDTH * WIDE_CHAR_MULTIPLIER);

          // UI Element Widths:
          // - 40px for items with dropdowns/submenus (menu/folder/preset icons)
          // - 24px for regular navigation items
          // - 24px additional spacing/padding per item
          const iconWidth = (item.menu || item.folder || item.preset) ? 40 : 24;
          const padding = 24;
          
          totalNaturalWidth += estimatedTextWidth + iconWidth + padding;
        });
      }
      // Overflow Detection: Compare calculated total width against available space
      const isOverflowing = totalNaturalWidth > availableWidth;

      // Layout Mode Decision:
      // - Overflowing: Switch to constrained mode with equal width distribution
      // - Not overflowing: Items use their natural calculated width
      const itemWidth = isOverflowing && _Nav.primary && _Nav.primary.length > 0
        ? Math.floor(availableWidth / _Nav.primary.length)
        : 0;
      
      setNavLayout({ isOverflowing, itemWidth, availableWidth });
    }

    if (isLargeScreen && hasPrivateAccess && !isUserLoading) {
      setTimeout(calculateNavLayout, 0);
    }
    
    function handleResize() {
      setTimeout(calculateNavLayout, 0);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isLargeScreen, hasPrivateAccess, isUserLoading, _Nav.primary]);

  return { headerRef, logoRef, navRef, rightRef, navLayout };
}
